import { Schema } from "effect"
import { Rpc, RpcGroup } from "effect/rpc"
import {
  AgentByOldHandleAnswer,
  AgentHistoryFilter,
  AgentHistoryPage,
  AgentNowView,
  MemoryHistory,
} from "../agent-page"
import { CommandEnvelope, CommandResult } from "../commands/index"
import { Actor, EntityRef } from "../common"
import { Comment } from "../domain/knowledge"
import { KpiHistory, KpiSample } from "../domain/plan"
import { Proposal } from "../domain/proposal"
import { PolicyStamp, ProjectEvent, ProjectEventEnvelope } from "../events"
import {
  AgentId,
  ChangesetId,
  Handle,
  ItemId,
  KpiId,
  Markdown,
  MemoryItemId,
  Millis,
  NonNegInt,
  PosInt,
  ProposalId,
  RunId,
  Seq,
} from "../ids"
import {
  DecidedHeader,
  HistoryKind,
  KnowledgeQuery,
  LookBackResult,
  SearchKind,
  SearchResult,
} from "../knowledge-search"
import { Run } from "../run"
import { AgentPresence, PresenceEntry, ProjectState, RunActivity } from "../state"

// #region project-sync
/** Frames on the project stream. Events always arrive as whole-command batches: a client never renders half a changeset. */
export const SyncFrame = Schema.TaggedUnion({
  Snapshot: { state: ProjectState }, // state.seq is the snapshot position
  Events: { events: Schema.Array(ProjectEventEnvelope) },
  /**
   * Ephemeral, never logged. A frame WITH `runs`/`runsRemoved` is a delta for runs only (fix wave §4.3): its
   * `people`/`agents` are sent empty and clients must ignore them.
   */
  Presence: {
    people: Schema.Array(PresenceEntry),
    agents: Schema.Array(AgentPresence),
    /** Upserts, newest per runId. */
    runs: Schema.optionalKey(Schema.Array(RunActivity)),
    runsRemoved: Schema.optionalKey(Schema.Array(RunId)),
  },
  Resync: { reason: Schema.Literals(["too_far_behind", "gap", "schema_upgrade", "access_changed"]) },
})
export type SyncFrame = typeof SyncFrame.Type
export type PresenceFrame = Extract<SyncFrame, { readonly _tag: "Presence" }>

/**
 * Decision 0151: `GET /api/workspaces/:ws/projects/:id/snapshot`, the frames `Subscribe{fromSeq: null}` sends first
 * (a `Snapshot`, then a runs-only `Presence`), over HTTP, so a page renders before its socket is open.
 */
export const SyncSnapshot = Schema.Array(SyncFrame)
export type SyncSnapshot = typeof SyncSnapshot.Type

/** `ProjectRpcs.Runs` filter. `project`: all runs, newest requested first; `run`: that one run (fix wave §4.4). */
export const RunsFilter = Schema.TaggedUnion({
  item: { itemId: ItemId },
  agent: { agentId: AgentId },
  project: {},
  run: { runId: RunId },
})
export type RunsFilter = typeof RunsFilter.Type

export const DryRunResult = Schema.Struct({
  result: CommandResult, // what Dispatch would return now
  events: Schema.Array(ProjectEvent), // what it would append (ghost preview for agents)
  verdicts: Schema.Array(PolicyStamp), // per change, for plan-diff cards
})

export const PlanDiff = Schema.Struct({
  from: Schema.Int,
  to: Schema.Int,
  changes: Schema.Array(
    Schema.Struct({
      ref: EntityRef,
      op: Schema.Literals(["added", "removed", "changed"]),
      fields: Schema.Array(Schema.String),
      changesetIds: Schema.Array(ChangesetId),
    }),
  ),
})

/** Decision 0051: one revision of the brief (Project.description), from the event that wrote it. */
export const BriefRevision = Schema.Struct({
  /** 1 = the first text the brief had. */
  rev: PosInt,
  /** The event that wrote it. */
  seq: Seq,
  /** The event's ts. */
  at: Millis,
  /** The event's actor. */
  by: Actor,
  /** `EventMeta.approvedBy.by`: the person who approved an agent's card; null when applied directly. */
  approvedBy: Schema.NullOr(Actor),
  body: Markdown,
  before: Markdown,
  why: Schema.NullOr(Schema.String),
})
export type BriefRevision = typeof BriefRevision.Type

/** Decision 0051: `ProjectRpcs.BriefHistory`'s answer; `total` counts every revision, `revisions` newest first. */
export const BriefHistory = Schema.Struct({ total: NonNegInt, revisions: Schema.Array(BriefRevision) })
export type BriefHistory = typeof BriefHistory.Type

/**
 * One WebSocket per open project to its Project DO (hibernating). Auth = the Better Auth session on the upgrade;
 * the actor is stamped from it. Subscribe{fromSeq: null | too old} → Snapshot, else an Events replay; then live.
 */
export class ProjectRpcs extends RpcGroup.make(
  Rpc.make("Subscribe", { payload: { fromSeq: Schema.NullOr(Seq) }, success: SyncFrame, stream: true }),
  Rpc.make("Dispatch", { payload: CommandEnvelope, success: CommandResult }),
  Rpc.make("DryRun", { payload: CommandEnvelope, success: DryRunResult }),
  Rpc.make("SetPresence", { payload: { focus: Schema.Array(EntityRef) } }),
  Rpc.make("History", {
    payload: { ref: EntityRef, beforeSeq: Schema.NullOr(Seq), limit: Schema.Int },
    success: Schema.Array(ProjectEventEnvelope),
  }),
  Rpc.make("Thread", { payload: { target: EntityRef }, success: Schema.Array(Comment) }),
  /**
   * Decision 0049: the newest comments of one kind across the project (≤ 100), newest first. Decision 0051:
   * `perTarget`: the newest comment of the kind on every target, newest first, never truncated (`limit` is ignored).
   */
  Rpc.make("Comments", {
    payload: {
      kind: Schema.Literals(["update", "decision"]),
      limit: Schema.Int,
      perTarget: Schema.optionalKey(Schema.Literal(true)),
    },
    success: Schema.Array(Comment),
  }),
  /** Decision 0051: the brief's revisions (Project.description), newest first, at most 50; `total` counts them all. */
  Rpc.make("BriefHistory", { payload: { limit: Schema.Int }, success: BriefHistory }),
  Rpc.make("PlanDiff", { payload: { from: Schema.Int, to: Schema.Int }, success: PlanDiff }),
  Rpc.make("Runs", {
    payload: {
      filter: RunsFilter,
      beforeSeq: Schema.NullOr(Seq),
      limit: Schema.Int,
    },
    success: Schema.Array(Run),
  }),
  Rpc.make("KpiSeries", {
    payload: { kpiId: KpiId, from: Millis, to: Millis },
    success: Schema.Array(KpiSample),
  }),
  /** Decision 0097: a KPI's samples with who recorded them and how, and what happened around it, in [from, to]. */
  Rpc.make("KpiHistory", {
    payload: { kpiId: KpiId, from: Millis, to: Millis },
    success: KpiHistory,
  }),
  // ---- wave K (decision 0065): search, history, decided cards. None of them hydrates the project state. ----
  /** Search the project's records (+ at most three team chat hits). Logged in the usage log. */
  Rpc.make("Search", {
    payload: {
      q: KnowledgeQuery,
      kinds: Schema.optionalKey(Schema.Array(SearchKind)),
      limit: Schema.optionalKey(Schema.Int),
    },
    success: SearchResult,
  }),
  /** Past discussions and runs: team chat messages and worker/validator transcripts by words and date. Logged. */
  Rpc.make("LookBack", {
    payload: {
      q: KnowledgeQuery,
      since: Schema.optionalKey(Millis),
      until: Schema.optionalKey(Millis),
      kinds: Schema.optionalKey(Schema.Array(HistoryKind)),
      limit: Schema.optionalKey(Schema.Int),
    },
    success: LookBackResult,
  }),
  /** The person opened hit `ref` of search `searchId` (usage log "followed"). Fire and forget. */
  Rpc.make("SearchOpened", { payload: { searchId: Schema.String, ref: Schema.String } }),
  /** K0: every decided card's header, newest first (ref chips, ⌘K). */
  Rpc.make("DecidedIndex", {
    payload: {},
    // `building`: the decided projection is still being backfilled from the log (refetch while it is set)
    success: Schema.Struct({ headers: Schema.Array(DecidedHeader), building: Schema.Boolean }),
  }),
  /** Full decided cards: by ids (≤ 50), else the newest `limit` (≤ 50) resolved before `before`. */
  Rpc.make("DecidedCards", {
    payload: {
      ids: Schema.optionalKey(Schema.Array(ProposalId)),
      limit: Schema.optionalKey(Schema.Int),
      before: Schema.optionalKey(Millis),
    },
    success: Schema.Struct({ cards: Schema.Array(Proposal), building: Schema.Boolean }),
  }),
  /** Read one anchor for the results page (a comment, a brief revision, a chat or transcript message ± 5). */
  Rpc.make("ReadAnchor", { payload: { anchor: Schema.String }, success: Schema.Json }),
  /** Owners and admins: mark both indexes dirty; an outbox job rebuilds records and history (spec §5.7). Answers at once. */
  Rpc.make("RebuildSearch", { payload: {}, success: Schema.Struct({ queued: Schema.Boolean }) }),
  /** Owners and admins: delete one ended worker/validator run's kept transcript (Run DO purge + its history rows). */
  Rpc.make("DeleteRunTranscript", {
    payload: { runId: RunId },
    success: Schema.Struct({ deleted: Schema.Boolean }),
  }),
  // ---- wave C (decision 0071): the agent page. Every member (viewers too) may read them. ----
  /** Now: core `agentOverview` over state, her recent turn runs (`Run.wakeReasons`) and comments on her items. */
  Rpc.make("AgentNow", { payload: { agentId: AgentId }, success: AgentNowView }),
  /** History: her runs newest first, whole days per page; `before` = the previous page's `nextBefore`. */
  Rpc.make("AgentHistory", {
    payload: {
      agentId: AgentId,
      before: Schema.optionalKey(Millis),
      filter: Schema.optionalKey(AgentHistoryFilter),
    },
    success: AgentHistoryPage,
  }),
  /** A memory's revisions from the event log (`events_tag_idx`), newest first. */
  Rpc.make("MemoryHistory", { payload: { memoryId: MemoryItemId }, success: MemoryHistory }),
  /** The agent whose earlier handle this was (renamed handles keep their links working). */
  Rpc.make("AgentByOldHandle", { payload: { handle: Handle }, success: AgentByOldHandleAnswer }),
) {}
// #endregion project-sync
